import React from 'react';

import type * as api from '@gitbook/api-client';
import type * as analytics from '@gitbook/app-analytics/client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { PageWithSidePanel, SidePanelSkeleton } from '@gitbook/spine-react';

import { GitSyncSidePanel } from '../../../Components';
import { useNavigation } from '../../../navigation';
// import { useBrokenLinksFeatureAccess } from '../../../state';
import { ChangeRequestAgentSidePanel } from '../Agent';
import { ChangeRequestSidePanel, ChangeRequestsListSidePanel } from '../ChangeRequest';
import { SpaceCommentsSidePanel } from '../Comments';
import { HistorySidePanel } from '../History';
import { PageOptionsSidePanel } from '../Page';
import { SpaceScreenBodySkeleton } from '../Skeleton';
import { VariablesSidePanel } from '../Variables';
import { WithSpaceHiveContext } from './WithSpaceHiveContext';

/**
 * Centralized controller for the side panels visible on the Space screen.
 *
 * It handles side panels that are driven by navigation, as well as any in-memory side panels.
 */
export function SpaceScreenSidePanel(props: {
    space: api.Space;
    analyticsContext: analytics.AnalyticsContextSpaceContent;
    children: React.ReactNode;
    screen: NavigationScreenSpaceState;
    changeRequest: api.ChangeRequest | null;
    readOnly: boolean;
    showGitSyncErrorModal: boolean;
}): React.ReactElement {
    const { space, changeRequest, screen, analyticsContext, children, readOnly } = props;
    const { navigate } = useNavigation();
    // const hasBrokenLinksFeature = useBrokenLinksFeatureAccess({ isInChangeRequest: false });

    const onCloseSideSheet = () => {
        navigate({
            current: {
                sideSheet: undefined,
                revision: undefined,
            },
        });
    };

    // Determine the current side panel, based on the navigation screen
    // and other state.
    const sidePanel: React.ReactNode | null = (() => {
        if (
            screen.revision &&
            // https://linear.app/gitbook-x/issue/RND-1988/explore-allowing-readers-to-access-history-and-change-requests
            space.permissions.edit
        ) {
            return (
                <HistorySidePanel
                    space={space}
                    changeRequest={changeRequest}
                    screen={screen}
                    analyticsContext={analyticsContext}
                />
            );
        }

        if (screen.sideSheet?.type === 'audits') {
            // We're temporarily disabling the audits side panel
            // because content refs indexation's current implementation is causing
            // huge performance issues on our PlanetScale DB.
            return null;

            // if (!hasBrokenLinksFeature) {
            //     return null;
            // }
            // return (
            //         <AuditsSidePanel
            //             changeRequest={changeRequest}
            //             space={space}
            //             screen={screen}
            //             analyticsContext={analyticsContext}
            //         />
            //     );
        }

        if (
            space.gitSync?.installationProvider &&
            screen.sideSheet?.type === 'gitsync' &&
            screen.sideSheet.tab !== 'progress' &&
            space.permissions.admin
        ) {
            return (
                <GitSyncSidePanel
                    space={space}
                    screen={screen}
                    analyticsContext={analyticsContext}
                />
            );
        }

        if (changeRequest && screen.sideSheet?.type === 'overview') {
            return <ChangeRequestSidePanel changeRequest={changeRequest} space={space} />;
        }

        if (changeRequest && screen.sideSheet?.type === 'agent') {
            return (
                <WithSpaceHiveContext
                    space={space}
                    changeRequest={changeRequest}
                    readOnly={readOnly}
                    screen={screen}
                >
                    <ChangeRequestAgentSidePanel
                        changeRequest={changeRequest}
                        space={space}
                        analyticsContext={analyticsContext}
                        screen={screen}
                    />
                </WithSpaceHiveContext>
            );
        }

        if (
            screen.sideSheet?.type === 'change-requests' &&
            // https://linear.app/gitbook-x/issue/RND-1988/explore-allowing-readers-to-access-history-and-change-requests
            space.permissions.edit
        ) {
            return (
                <ChangeRequestsListSidePanel space={space} analyticsContext={analyticsContext} />
            );
        }

        if (
            screen.sideSheet?.type === 'comments' &&
            space.permissions.comment &&
            screen.sideSheet.context !== 'preview'
        ) {
            return (
                <React.Suspense fallback={<SidePanelSkeleton onClose={onCloseSideSheet} />}>
                    <WithSpaceHiveContext
                        space={space}
                        changeRequest={changeRequest}
                        readOnly={readOnly}
                        screen={screen}
                    >
                        <SpaceCommentsSidePanel
                            space={space}
                            changeRequest={changeRequest}
                            analyticsContext={analyticsContext}
                            screen={screen}
                        />
                    </WithSpaceHiveContext>
                </React.Suspense>
            );
        }

        if (screen.sideSheet?.type === 'page' && !readOnly) {
            return (
                <WithSpaceHiveContext
                    space={space}
                    changeRequest={changeRequest}
                    readOnly={readOnly}
                    screen={screen}
                >
                    <PageOptionsSidePanel
                        space={space}
                        analyticsContext={analyticsContext}
                        readOnly={readOnly}
                        screen={{
                            ...screen,
                            sideSheet: screen.sideSheet,
                        }}
                    />
                </WithSpaceHiveContext>
            );
        }

        if (screen.sideSheet?.type === 'variables') {
            return (
                <WithSpaceHiveContext
                    space={space}
                    changeRequest={changeRequest}
                    readOnly={readOnly}
                    screen={screen}
                >
                    <VariablesSidePanel
                        analyticsContext={analyticsContext}
                        screen={screen}
                        readOnly={readOnly}
                    />
                </WithSpaceHiveContext>
            );
        }

        return null;
    })();

    return (
        <PageWithSidePanel
            sidePanel={
                sidePanel ? (
                    <React.Suspense fallback={<SidePanelSkeleton onClose={onCloseSideSheet} />}>
                        {sidePanel}
                    </React.Suspense>
                ) : null
            }
        >
            <React.Suspense fallback={<SpaceScreenBodySkeleton />}>{children}</React.Suspense>
        </PageWithSidePanel>
    );
}

/**
 * Return true if the space screen should display a side panel.
 */
export function shouldDisplaySidePanel(screen: NavigationScreenSpaceState): boolean {
    return (
        !!screen.revision ||
        screen.sideSheet?.type === 'page' ||
        (screen.sideSheet?.type === 'gitsync' && screen.sideSheet.tab !== 'progress') ||
        // screen.sideSheet?.type === 'audits' ||
        screen.sideSheet?.type === 'overview' ||
        screen.sideSheet?.type === 'agent' ||
        screen.sideSheet?.type === 'change-requests' ||
        (screen.sideSheet?.type === 'comments' && screen.sideSheet.context !== 'preview') ||
        screen.sideSheet?.type === 'variables'
    );
}
